import clsx from "clsx";
import RootLayout from "layouts/RootLayout";
import React, { useState, useEffect, useCallback } from "react";
import Select from "react-select";
import { getClinicList } from "apis/clinicService";
import { getEmergencyWaitInfo } from "apis/emergencyService";
import type { EmergencyItem } from "apis/emergencyService";
import BasicModal from "components/BasicModal";
import EmergencyModal from "components/EmergencyModal";
import { useTranslation } from "react-i18next";

const ClinicList = () => {
  const { t } = useTranslation("clinic");
  // 篩選/badge 顯示文字：value 維持中文 canonical（比對/送出用），label 經此翻譯
  const areaLabel = (v: string) => (v === '' ? t('filter.all') : v);
  const typeLabel = (v: string) => {
    if (v === '') return t('filter.all');
    if (v === '幼兒專責機構') return t('badge.childCare');
    if (v === '塗氟診所') return t('badge.fluoride');
    if (v === '兒童發展篩檢服務院所') return t('badge.childDevelopment');
    return v;
  };
  const [clinicList, setClinicList] = useState([]);
  const [areaOptions, setAreaOptions] = useState([]);
  const [areaOption, setAreaOption] = useState({ label: '全部', value: '' });
  const [typeOptions, setTypeOptions] = useState([]);
  const [typeOption, setTypeOption] = useState({ label: '全部', value: '' });
  const [searchKeyword, setSearchKeyword] = useState("");
  const [filteredClinics, setFilteredClinics] = useState([]);
  // 急診即時訊息：key = hospitalId(= facilities.code)
  const [emergencyMap, setEmergencyMap] = useState<Record<string, EmergencyItem>>({});
  const [emergencyUpdatedAt, setEmergencyUpdatedAt] = useState('');

  useEffect(() => {
    const fetchClinicList = async () => {
      try {
        const data = await getClinicList();
        setClinicList(data);

        const uniqueAreas = Array.from(new Set(data.map(clinic => clinic.area)));
        const areaOptions = [{ label: '全部', value: '' }, ...uniqueAreas.map(area => ({ label: area, value: area }))];
        setAreaOptions(areaOptions);
      } catch (error) {
        console.error('Error fetching clinic list:', error);
      }
    };

    fetchClinicList();
  }, []);

  // 進頁面即時取得急診待診資訊（有資料的醫院才會在卡片顯示項目）
  useEffect(() => {
    getEmergencyWaitInfo().then((result) => {
      const map: Record<string, EmergencyItem> = {};
      result.items.forEach((item) => {
        if (item.hospitalId) map[item.hospitalId] = item;
      });
      setEmergencyMap(map);
      setEmergencyUpdatedAt(result.updatedAt);
    });
  }, []);

  const getTypeOptionsForArea = useCallback((area: string) => {
    const selectedClinicList = area ? clinicList.filter(c => c.area === area) : clinicList;
    if (!selectedClinicList) return [];

    const types = new Set(['幼兒專責機構', '塗氟診所', '兒童發展篩檢服務院所']);
    selectedClinicList.filter(clinic => clinic).forEach(c => types.add(c.type));

    return [
      { label: '全部', value: '' },
      ...Array.from(types).map(type => ({ label: type, value: type }))
    ];
  }, [clinicList]);

  useEffect(() => {
    const options = getTypeOptionsForArea(areaOption.value);
    setTypeOptions(options);
  }, [areaOption, clinicList, getTypeOptionsForArea]);

  useEffect(() => {
    const selectedClinicList = areaOption.value
      ? clinicList.filter(c => c.area === areaOption.value)
      : clinicList;

    if (selectedClinicList) {
      let clinics = selectedClinicList;

      if (typeOption.value === '幼兒專責機構') {
        clinics = clinics.filter(clinic => clinic.isChildCare);
      } else if (typeOption.value === '塗氟診所') {
        clinics = clinics.filter(clinic => clinic.isFluoride);
      } else if (typeOption.value === '兒童發展篩檢服務院所') {
        clinics = clinics.filter(clinic => clinic.isChildDevelopment);
      } else if (typeOption.value) {
        clinics = clinics.filter(clinic => clinic.type === typeOption.value);
      }

      if (searchKeyword) {
        clinics = clinics.filter(clinic =>
          clinic.name.includes(searchKeyword) ||
          clinic.address.includes(searchKeyword)
        );
      }

      clinics.sort((a, b) => {
        if (a.isChildCare && !b.isChildCare) return -1;
        if (!a.isChildCare && b.isChildCare) return 1;
        if (a.isFluoride && !b.isFluoride) return -1;
        if (!a.isFluoride && b.isFluoride) return 1;
        if (a.isChildDevelopment && !b.isChildDevelopment) return -1;
        if (!a.isChildDevelopment && b.isChildDevelopment) return 1;
        return 0;
      });

      setFilteredClinics(clinics);
    }
  }, [searchKeyword, areaOption, typeOption, clinicList]);

  const handleOpenGoogleMap = (address: string, map: string) => {
    const encodedAddress = encodeURIComponent(address);
    const googleMapsUrl = map || `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`;
    window.open(googleMapsUrl, '_blank', 'noopener,noreferrer');
  }

  return (
    <RootLayout title={t("list.title")}>
      <div className="mx-7 mt-1.5 mb-9 space-y-3">
        <Select
          className="text-2xl bg-[#E0E0E0]"
          options={areaOptions.map(o => ({ value: o.value, label: areaLabel(o.value) }))}
          onChange={option => {
            setAreaOption(option);
          }}
          value={{ value: areaOption.value, label: areaLabel(areaOption.value) }}
          isSearchable={false}
        />
        <Select
          className="text-2xl bg-[#E0E0E0]"
          options={typeOptions.map(o => ({ value: o.value, label: typeLabel(o.value) }))}
          onChange={option => {
            setTypeOption(option);
          }}
          value={{ value: typeOption.value, label: typeLabel(typeOption.value) }}
          isSearchable={false}
        />
        <input
          type="text"
          placeholder={t("search.placeholder")}
          value={searchKeyword}
          onChange={e => setSearchKeyword(e.target.value)}
          className="w-full p-2 border border-gray-300 rounded"
        />
        {filteredClinics.map(c => (
          <div
            key={c.code}
            className={clsx(
              "p-[5px] flex flex-col space-y-[5px]",
              "border border-[#00000033] rounded-[5px]",
              "text-[#555555] font-normal",
              "overflow-auto"
            )}
          >
            <div
              className={clsx(
                "px-5 flex items-center flex-1",
                "bg-[#FCE4EC7F] rounded-t-[5px]",
                "text-xl text-[#0000ff]"
              )}
            >
              {c.name}
              {c.isChildCare === true &&
                <div className="ml-2 px-2 py-1 bg-[#FFD700] rounded-[5px] text-xs">
                  {t('badge.childCare')}
                </div>
              }
              {c.isFluoride === true &&
                <div className="ml-2 px-2 py-1 bg-[#FFD700] rounded-[5px] text-xs">
                  {t('badge.fluoride')}
                </div>
              }
              {c.isChildDevelopment === true &&
                <div className="ml-2 px-2 py-1 bg-[#FFD700] rounded-[5px] text-xs">
                  {t('badge.childDevelopment')}
                </div>
              }
            </div>
            <div
              className={clsx(
                "px-2.5 flex items-center flex-1",
                "bg-[#0000000F] text-lg"
              )}
            >
              <span className="mr-1">
                <img src="/img/phone.png" alt=""/>
              </span>
              <span>{c.telephone}</span>
            </div>
            <div
              className={clsx(
                "px-2.5 flex items-center justify-between flex-1",
                "bg-[#FCE4EC80]"
              )}
            >
              <div className="flex items-center">
                <span className="mr-1">
                  <img src="/img/entypo_address.png" alt=""/>
                </span>
                <span>{c.address}</span>
              </div>
              <div onClick={() => handleOpenGoogleMap(c.address, c.map)}>
                <img src="/img/google-maps.png" alt=""/>
              </div>
            </div>
            <div
              className={clsx(
                "px-2.5 flex items-center flex-1",
                "bg-[#0000000F] text-lg"
              )}
            >
              <BasicModal title={`${c.name} ${t('serviceTimeSuffix')}`} serviceInfo={c.serviceInfo ?? undefined}></BasicModal>
            </div>
            {emergencyMap[c.code] && (
              <div
                className={clsx(
                  "px-2.5 flex items-center flex-1",
                  "bg-[#FCE4EC80] text-lg"
                )}
              >
                <EmergencyModal
                  title={`${c.name} ${t('emergencyTitleSuffix')}`}
                  data={emergencyMap[c.code]}
                  updatedAt={emergencyUpdatedAt}
                />
              </div>
            )}
            {c.bookingUrl && (
              <div
                className={clsx(
                  "px-2.5 flex items-center flex-1",
                  "bg-[#FCE4EC80] text-lg cursor-pointer"
                )}
                onClick={() => window.open(c.bookingUrl, '_blank', 'noopener,noreferrer')}
              >
                <span className="text-[#0000ff]">{t('booking')}</span>
              </div>
            )}
          </div>
        ))}
      </div>
    </RootLayout>
  );
};

export default ClinicList;